import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Package,
  Settings,
  LogOut,
  User,
  Users as UsersIcon,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  Car,
  Wallet,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children, pageTitle }) => {
  const { user, userRole, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isStockMenuOpen, setIsStockMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const mainNavigationBeforeStock = [
    { name: 'ÖZET', href: '/', icon: Home, permission: 'dashboard' },
    { name: 'MÜŞTERİLER', href: '/customers', icon: UsersIcon, permission: 'customers' },
  ].filter(item => hasPermission(item.permission));

  const mainNavigationAfterStock = [
    { name: 'GÜNLÜK İŞLEMLER', href: '/transactions', icon: TrendingUp, permission: 'transactions' },
    { name: 'ÇALIŞAN PRİMLERİ', href: '/bonuses', icon: Wallet, permission: 'settings' },
  ].filter(item => hasPermission(item.permission));

  const stockSubMenu = [
    { name: 'STOK YÖNETİMİ', href: '/products', icon: Package },
    { name: 'ARAÇLAR', href: '/cars', icon: Car },
  ];

  const secondaryNavigation = [
    { name: 'AYARLAR', href: '/settings', icon: Settings, permission: 'settings' },
  ].filter(item => !item.permission || hasPermission(item.permission));

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const result = await logout();
      if (result.success) {
        // Auth state change will automatically redirect via PermissionRoute
        // Navigate to login as fallback after a short delay to ensure state is cleared
        setTimeout(() => {
          navigate('/login');
        }, 100);
      }
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const isActive = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const isStockActive = () => {
    return location.pathname.startsWith('/products') || location.pathname.startsWith('/cars');
  };

  const toggleStockMenu = () => {
    setIsStockMenuOpen(!isStockMenuOpen);
  };

  const sidebarWidth = isSidebarCollapsed && !isHovered ? 'w-16' : 'w-64';
  const showText = !isSidebarCollapsed || isHovered;

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Desktop Sidebar - Hidden on mobile */}
      <div
        className={`hidden lg:flex fixed inset-y-0 left-0 ${sidebarWidth} flex-col transition-all duration-300 ease-in-out`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex h-16 items-center px-4">
            {showText && (
              <h1 className="text-2xl font-bold text-gray-900 tracking-wider">ÇELİKTAŞ</h1>
            )}
          </div>
          <nav className="flex-1 flex flex-col justify-between px-3 py-4">
                <div>
                  {/* Stok Menüsünden Önceki Menüler */}
                  {mainNavigationBeforeStock.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsSidebarCollapsed(true)}
                        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                          isActive(item.href)
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={!showText ? item.name : ''}
                      >
                        <Icon className={`h-5 w-5 ${showText ? 'mr-3' : 'mx-auto'}`} />
                        {showText && <span className="truncate">{item.name}</span>}
                      </Link>
                    );
                  })}
                  
                  {/* Stok Menüsü */}
                  {hasPermission('products') && (
                    <div className="mb-2">
                      <button
                        onClick={() => {
                          if (showText) {
                            toggleStockMenu();
                          } else {
                            setIsSidebarCollapsed(false);
                            setIsStockMenuOpen(true);
                          }
                        }}
                        className={`group flex items-center ${showText ? 'justify-between' : 'justify-center'} w-full px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                          isStockActive()
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={!showText ? 'Stok' : ''}
                      >
                        <div className="flex items-center">
                          <Package className={`h-5 w-5 ${showText ? 'mr-3' : 'mx-auto'}`} />
                          {showText && <span className="truncate">STOK</span>}
                        </div>
                        {showText && (
                          isStockMenuOpen ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )
                        )}
                      </button>
                      
                      {/* Alt Menü */}
                      {isStockMenuOpen && showText && (
                        <div className="ml-6 mt-1 space-y-1">
                          {stockSubMenu.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setIsSidebarCollapsed(true)}
                                className={`group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  isActive(item.href)
                                    ? 'bg-primary-50 text-primary-700'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }`}
                              >
                                <Icon className="mr-3 h-4 w-4" />
                                <span className="truncate">{item.name}</span>
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Stok Menüsünden Sonraki Menüler */}
                  {mainNavigationAfterStock.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setIsSidebarCollapsed(true)}
                        className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                          isActive(item.href)
                            ? 'bg-primary-100 text-primary-900'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={!showText ? item.name : ''}
                      >
                        <Icon className={`h-5 w-5 ${showText ? 'mr-3' : 'mx-auto'}`} />
                        {showText && <span className="truncate">{item.name}</span>}
                      </Link>
                    );
                  })}
                </div>
            <div>
              {secondaryNavigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsSidebarCollapsed(true)}
                    className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${
                      isActive(item.href)
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={!showText ? item.name : ''}
                  >
                    <Icon className={`h-5 w-5 ${showText ? 'mr-3' : 'mx-auto'}`} />
                    {showText && <span className="truncate">{item.name}</span>}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={`flex-1 flex flex-col min-h-0 transition-all duration-300 ease-in-out ${isSidebarCollapsed && !isHovered ? 'lg:pl-16' : 'lg:pl-64'}`}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm">
          <div className="flex flex-1 gap-x-4 self-stretch">
            <div className="flex flex-1 items-center">
              {pageTitle && (
                <h1 className="text-2xl font-bold text-gray-900 ml-2 truncate">
                  {pageTitle}
                </h1>
              )}
            </div>
            <div className="flex items-center gap-x-4">
              {/* User menu */}
              <div className="relative">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 bg-gray-50 px-3 py-2 rounded-lg">
                    <User size={18} className="text-primary-600 w-5 h-5" />
                    <div className="hidden lg:flex flex-col">
                      <span className="text-sm font-semibold text-gray-800 truncate max-w-32">
                        {user?.user_metadata?.full_name ||
                         user?.user_metadata?.name ||
                         user?.email?.split('@')[0] ||
                         'Kullanıcı'}
                      </span>
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 truncate max-w-24">
                          {user?.email}
                        </span>
                        <span className="text-xs px-1 py-0.5 bg-primary-100 text-primary-700 rounded text-center min-w-0">
                          {userRole === 'admin' ? 'Admin' :
                           userRole === 'warehouse' ? 'Depo' :
                           userRole === 'reception' ? 'Resepsiyon' : 'Kullanıcı'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 px-2 py-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoggingOut ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-1"></div>
                  ) : (
                  <LogOut size={16} className="w-4 h-4" />
                  )}
                  <span className="hidden lg:inline">{isLoggingOut ? 'Çıkış yapılıyor...' : 'Çıkış'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 flex flex-col min-h-0 pb-16 lg:pb-0">
          <div className="flex-1 flex flex-col min-h-0">
            {children}
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation - Visible only on mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex justify-around items-center h-16 px-2">
          {/* Home */}
          {hasPermission('dashboard') && (
            <Link
              to="/"
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive('/') ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              <Home className="h-5 w-5" />
              <span className="text-xs mt-1">ÖZET</span>
            </Link>
          )}

          {/* Customers */}
          {hasPermission('customers') && (
            <Link
              to="/customers"
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive('/customers') ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              <UsersIcon className="h-5 w-5" />
              <span className="text-xs mt-1">MÜŞTERİLER</span>
            </Link>
          )}

          {/* Stock - Products */}
          {hasPermission('products') && (
            <Link
              to="/products"
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive('/products') ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              <Package className="h-5 w-5" />
              <span className="text-xs mt-1">STOK</span>
            </Link>
          )}

          {/* Transactions */}
          {hasPermission('transactions') && (
            <Link
              to="/transactions"
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive('/transactions') ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              <TrendingUp className="h-5 w-5" />
              <span className="text-xs mt-1">İŞLEMLER</span>
            </Link>
          )}

          {/* More/Settings */}
          <div className="relative">
            <button
              onClick={() => {
                // Toggle mobile menu or navigate to settings
                navigate('/settings');
              }}
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-colors ${
                isActive('/settings') ? 'text-primary-600' : 'text-gray-500'
              }`}
            >
              <Settings className="h-5 w-5" />
              <span className="text-xs mt-1">DAHA</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;
